import { Upload, FileText, AlertTriangle, Layers, Shield, ShieldAlert, X } from 'lucide-react';
import { timeAgo } from '../../../utils/dateHelpers';

/**
 * Per-case AtWork screenshot upload list. Replaces the single-screenshot
 * upload block in the sidebar when a file has caseCount IS NOT NULL.
 *
 * Each row corresponds to a case (case_index 0..caseCount-1) and shows
 * either an "Upload screenshot" button or a thumbnail + "Replace" button
 * once a screenshot has been uploaded. Clicking the thumbnail opens the
 * full-size image via the parent's lightbox.
 *
 * The "Move to completed" button is disabled until every case has been
 * uploaded; the helper line below shows live progress.
 */
export default function CaseScreenshotsPanel({
  file,
  caseLabels,
  caseCount,
  screenshotsByIndex,
  uploadedCount,
  allUploaded,
  onUpload,
  uploadingIndex,
  uploadStage,
  rowErrors,
  onDismissRowError,
  onRequestOverride,
  onOpenFlagDetails,
  isAdviser,
  onOpenLightbox,
  onMoveToCompleted,
  actionLoading,
}) {
  // Map per-row upload stage to a friendly label.
  const stageLabel = (idx) => {
    const stage = uploadStage && uploadStage[idx];
    if (stage === 'verifying') return 'Verifying…';
    if (stage === 'uploading') return 'Uploading…';
    return null;
  };

  return (
    <div style={panelStyles.wrapper}>
      <div style={panelStyles.header}>
        <h4 style={panelStyles.title}>
          <Layers size={14} /> AtWork submission proof
        </h4>
        <p style={panelStyles.helper}>
          Upload a screenshot of the AtWork query for each case to confirm it has been submitted to the product house. All screenshots required before the file can move to Completed.
        </p>
      </div>
      <ol style={panelStyles.list}>
        {Array.from({ length: caseCount }).map((_, idx) => {
          const shot = screenshotsByIndex[idx];
          const label = caseLabels[idx] || '';
          const isUploading = uploadingIndex === idx;
          const inFlightLabel = stageLabel(idx);
          const rowError = rowErrors && rowErrors[idx];
          const isFlagged = shot && shot.verificationStatus === 'flagged';
          const isOverridden =
            shot &&
            (shot.verificationStatus === 'l2_overridden'
              || shot.verificationStatus === 'flag_overridden');
          return (
            <li key={idx} style={panelStyles.row}>
              <div style={panelStyles.rowMeta}>
                <span style={panelStyles.rowTag}>Case {idx + 1}</span>
                <span style={panelStyles.rowLabel} title={label}>{label}</span>
                {isFlagged && (
                  <button
                    type="button"
                    style={panelStyles.flagPill}
                    title="Verification flagged — content may not match case label. Click for details."
                    onClick={() => onOpenFlagDetails(shot)}
                  >
                    <ShieldAlert size={12} /> Flag
                  </button>
                )}
                {isOverridden && (
                  <span
                    style={panelStyles.overridePill}
                    title={`Verified by advisor${shot.overrideBy ? ` (${shot.overrideBy})` : ''}: ${shot.overrideReason || ''}`}
                  >
                    <Shield size={12} /> verified by advisor
                  </span>
                )}
              </div>

              {/* Row body — thumbnail with controls, or upload button. */}
              {shot ? (
                <div style={panelStyles.rowDone}>
                  <img
                    src={shot.screenshotUrl}
                    alt={`Case ${idx + 1} screenshot`}
                    style={panelStyles.thumb}
                    onClick={() => onOpenLightbox(shot.screenshotUrl)}
                  />
                  <div style={panelStyles.rowDoneRight}>
                    <span style={panelStyles.uploadedAgo}>
                      Uploaded {timeAgo(shot.uploadedAt)}
                    </span>
                    <label style={panelStyles.replaceBtn}>
                      {isUploading ? (inFlightLabel || 'Replacing…') : 'Replace'}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => onUpload(idx, e)}
                        style={{ display: 'none' }}
                        disabled={isUploading || actionLoading}
                      />
                    </label>
                  </div>
                </div>
              ) : (
                <label style={panelStyles.uploadBtn}>
                  <Upload size={14} />
                  {isUploading ? (inFlightLabel || 'Uploading…') : 'Upload screenshot'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => onUpload(idx, e)}
                    style={{ display: 'none' }}
                    disabled={isUploading || actionLoading}
                  />
                </label>
              )}

              {/* Inline Layer 2 hard-reject error. Auto-clears when the
                  user picks a new file. Advisor gets an extra "Override"
                  affordance that opens the override-reason modal. */}
              {rowError && (
                <div style={panelStyles.rowError}>
                  <div style={panelStyles.rowErrorHead}>
                    <AlertTriangle size={14} color="#C0392B" />
                    <span style={{ flex: 1 }}>{rowError.message}</span>
                    <button
                      type="button"
                      style={panelStyles.rowErrorDismiss}
                      onClick={() => onDismissRowError(idx)}
                      aria-label="Dismiss"
                    >
                      <X size={12} />
                    </button>
                  </div>
                  {isAdviser && rowError.kind === 'l2_failed' && (
                    <button
                      type="button"
                      style={panelStyles.overrideBtn}
                      onClick={() => onRequestOverride(idx)}
                      disabled={actionLoading || isUploading}
                    >
                      Override and upload anyway
                    </button>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ol>
      <button
        type="button"
        className="btn btn-secondary"
        style={{
          ...panelStyles.completeBtn,
          ...(allUploaded ? {} : { opacity: 0.55, cursor: 'not-allowed' }),
        }}
        onClick={onMoveToCompleted}
        disabled={!allUploaded || actionLoading}
      >
        <FileText size={16} /> Move to Completed
      </button>
      <p
        style={{
          ...panelStyles.counter,
          color: allUploaded ? 'var(--cp-success)' : 'var(--cp-text-muted)',
        }}
      >
        {allUploaded
          ? 'All screenshots uploaded — ready to complete'
          : `${uploadedCount} of ${caseCount} screenshots uploaded`}
      </p>
    </div>
  );
}

const panelStyles = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.6rem',
    padding: '0.85rem',
    background: '#F7F9FC',
    border: '1px solid var(--cp-surface-alt)',
    borderRadius: '10px',
    marginBottom: '0.5rem',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    margin: 0,
    fontSize: '0.85rem',
    fontWeight: 600,
    color: 'var(--cp-primary)',
  },
  helper: {
    margin: 0,
    fontSize: '0.75rem',
    color: 'var(--cp-text-muted)',
    lineHeight: 1.4,
  },
  list: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
    padding: '0.55rem 0.65rem',
    background: 'var(--cp-surface)',
    border: '1px solid var(--cp-surface-alt)',
    borderRadius: '8px',
  },
  rowMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  rowTag: {
    flexShrink: 0,
    padding: '0.2rem 0.5rem',
    background: 'rgba(27, 154, 170, 0.12)',
    color: 'var(--cp-primary)',
    fontSize: '0.7rem',
    fontWeight: 600,
    borderRadius: '999px',
  },
  rowLabel: {
    fontSize: '0.8rem',
    color: 'var(--cp-text)',
    fontWeight: 500,
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  uploadBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.4rem',
    padding: '0.55rem 0.75rem',
    background: 'var(--cp-bg)',
    border: '1.5px dashed var(--cp-border)',
    borderRadius: '8px',
    fontSize: '0.8rem',
    color: 'var(--cp-primary)',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  rowDone: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
  },
  thumb: {
    width: '60px',
    height: '60px',
    objectFit: 'cover',
    borderRadius: '6px',
    border: '1px solid var(--cp-border)',
    cursor: 'pointer',
    flexShrink: 0,
  },
  rowDoneRight: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.3rem',
    flex: 1,
    minWidth: 0,
  },
  uploadedAgo: {
    fontSize: '0.7rem',
    color: 'var(--cp-text-muted)',
  },
  replaceBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.3rem',
    padding: '0.3rem 0.6rem',
    background: 'transparent',
    border: '1px solid var(--cp-border)',
    borderRadius: '6px',
    fontSize: '0.75rem',
    color: 'var(--cp-primary)',
    fontWeight: 500,
    cursor: 'pointer',
    alignSelf: 'flex-start',
  },
  completeBtn: {
    width: '100%',
    justifyContent: 'center',
    marginTop: '0.25rem',
  },
  counter: {
    margin: 0,
    fontSize: '0.75rem',
    textAlign: 'center',
    fontWeight: 500,
  },
  flagPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.2rem',
    padding: '0.15rem 0.45rem',
    borderRadius: '999px',
    border: 'none',
    background: 'rgba(244, 162, 97, 0.18)',
    color: '#8C5A2D',
    fontSize: '0.7rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  overridePill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.2rem',
    padding: '0.15rem 0.45rem',
    borderRadius: '999px',
    background: 'rgba(46, 196, 182, 0.14)',
    color: '#1F8E83',
    fontSize: '0.7rem',
    fontWeight: 600,
    cursor: 'help',
  },
  rowError: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
    padding: '0.5rem 0.6rem',
    background: 'rgba(192, 57, 43, 0.06)',
    border: '1px solid rgba(192, 57, 43, 0.25)',
    borderRadius: '8px',
    fontSize: '0.78rem',
    color: 'var(--cp-text)',
  },
  rowErrorHead: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.4rem',
    lineHeight: 1.4,
  },
  rowErrorDismiss: {
    background: 'transparent',
    border: 'none',
    color: 'var(--cp-text-muted)',
    cursor: 'pointer',
    padding: '0.1rem',
    display: 'flex',
  },
  overrideBtn: {
    alignSelf: 'flex-start',
    padding: '0.3rem 0.65rem',
    background: 'transparent',
    border: '1px solid var(--cp-danger)',
    color: 'var(--cp-danger)',
    fontSize: '0.75rem',
    fontWeight: 600,
    borderRadius: '6px',
    cursor: 'pointer',
  },
};
